import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FlaskConical, Send, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSlip } from "@/context/SlipContext";
import { fmtOdds } from "@/lib/odds";

const KEY = "parlaylab_chat_session";
const SUGGEST = ["What's the best play on tonight's slate?", "Build me a safe 3-leg parlay", "Who's on a hot prop streak?", "How does my slip look?"];
const HIDDEN = ["/", "/login", "/register", "/pricing"];

export function AskLab() {
  const { user } = useAuth();
  const { legs, has, toggle } = useSlip();
  const add = (p) => { if (!has(p.pick_id)) toggle(p); };
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sid = useRef(localStorage.getItem(KEY) || `s-${Date.now().toString(36)}`);
  useEffect(() => { localStorage.setItem(KEY, sid.current); }, []);
  useEffect(() => { if (open) api.get(`/chat/${sid.current}`).then((r) => setMsgs(r.data.messages)).catch(() => {}); }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  if (!user || HIDDEN.includes(pathname) || pathname.startsWith("/t/") || pathname.startsWith("/p/")) return null;

  const send = async (q) => {
    const message = (q || text).trim();
    if (!message) return;
    setText("");
    setMsgs((m) => [...m, { message_id: `u-${Date.now()}`, role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { session_id: sid.current, message, slip: legs.map((l) => l.pick_id) });
      setMsgs((m) => [...m, data]);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const newSession = () => { sid.current = `s-${Date.now().toString(36)}`; localStorage.setItem(KEY, sid.current); setMsgs([]); };

  return (
    <>
      <button data-testid="ask-lab-fab" onClick={() => setOpen((o) => !o)} className="fixed bottom-24 right-4 z-40 flex items-center gap-2 rounded-full border border-cyan-400/50 bg-[#121824]/95 px-4 py-2.5 text-sm font-bold text-cyan-200 shadow-[0_0_30px_rgba(34,211,238,0.25)] backdrop-blur transition-transform hover:-translate-y-0.5 lg:bottom-6">
        <FlaskConical size={16} /> Ask the Lab
      </button>
      {open && (
        <div data-testid="ask-lab-panel" className="glass-card fade-up fixed bottom-40 right-4 z-50 flex h-[min(560px,70vh)] w-[min(400px,calc(100vw-2rem))] flex-col overflow-hidden !bg-[#0B0E14] shadow-2xl lg:bottom-20">
          <div className="flex items-center gap-2 border-b border-[#26334D] px-4 py-3">
            <FlaskConical size={16} className="text-cyan-400" /><span className="font-display whitespace-nowrap text-lg font-bold uppercase tracking-tight text-white">Lab Analyst</span>
            <span className="hidden text-[10px] uppercase tracking-wider text-slate-500 sm:inline">Claude · live slate</span>
            <button data-testid="ask-lab-new" onClick={newSession} className="ml-auto text-[10px] uppercase tracking-wider text-slate-500 hover:text-white">New chat</button>
            <button data-testid="ask-lab-close" onClick={() => setOpen(false)} className="rounded p-1 text-slate-400 hover:text-white"><X size={15} /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3" data-testid="ask-lab-messages">
            {msgs.length === 0 && (
              <div className="space-y-2" data-testid="ask-lab-suggestions">
                <p className="text-xs text-slate-400">Ask about tonight's slate, a player, your slip or your record — I can add plays straight to your slip.</p>
                {SUGGEST.map((s) => <button key={s} data-testid="ask-lab-suggestion" onClick={() => send(s)} className="block w-full rounded-lg border border-[#26334D] bg-[#121824] px-3 py-2 text-left text-xs text-slate-200 hover:border-cyan-400/60">{s}</button>)}
              </div>
            )}
            {msgs.map((m) => (
              <div key={m.message_id} data-testid={`chat-msg-${m.role}`} className={`max-w-[92%] rounded-xl px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-emerald-500/15 text-emerald-50" : "bg-[#121824] text-slate-200"}`}>
                {m.content}
                {m.picks?.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="chat-add-picks">
                    {m.picks.map((p) => <button key={p.pick_id} data-testid="chat-add-pick-btn" onClick={() => { add(p); toast.success(`${p.selection} added to slip`); }} className="flex w-full items-center gap-2 rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-2 py-1.5 text-left text-xs"><Plus size={12} className="text-cyan-300" /><span className="min-w-0 flex-1 truncate text-white">{p.selection}</span><span className="font-mono-num text-amber-400">{fmtOdds(p.odds)}</span></button>)}
                  </div>
                )}
              </div>
            ))}
            {busy && <div className="w-fit rounded-xl bg-[#121824] px-3 py-2 text-xs text-slate-400" data-testid="chat-typing">Reading the slate…</div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t border-[#26334D] p-3">
            <input data-testid="ask-lab-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={600} placeholder="Ask the analyst…" className="input-dark flex-1 !py-2 text-sm" />
            <button data-testid="ask-lab-send" disabled={busy || !text.trim()} className="btn-primary !px-3 !py-2"><Send size={14} /></button>
          </form>
        </div>
      )}
    </>
  );
}
